/**
 * Search components.
 *
 * @element search-drawer          – slide-out search drawer.
 * @element search-typed           – animated typewriter text for the search bar.
 * @element predictive-search      – live-search form with response caching.
 * @element predictive-search-overlay – backdrop that closes the search overlay.
 */

/**
 * Search Drawer
 * Extends DrawerElement; focuses the search input when opened.
 *
 * @element search-drawer
 * @extends DrawerElement
 */
if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      get shouldAppendToBody() { return false; }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

/**
 * Search Typed
 * Animates a typewriter effect between two text strings using Motion.
 *
 * @element search-typed
 */
if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      /** @type {HTMLElement|null} */ #cursor = null;

      get #startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      connectedCallback() {
        Motion.inView(this, () => this.#run());
      }

      async #run() {
        this.#insertCursor();
        await this.#type(this.getAttribute('data-first-text'), this.#startDelay);

        setTimeout(async () => {
          await this.#erase();
          await this.#type(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      /**
       * @param {string|null} text
       * @param {number} delay
       */
      async #type(text, delay) {
        this.innerHTML = text ?? '';
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.#cursor?.classList.add('blink');
      }

      async #erase() {
        this.#cursor?.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      #insertCursor() {
        if (this.#cursor) return;
        this.#cursor = document.createElement('span');
        this.#cursor.className = 'typed-cursor';
        this.#cursor.setAttribute('aria-hidden', 'true');
        this.#cursor.innerHTML = '|';
        this.parentElement?.insertBefore(this.#cursor, this.nextSibling);
      }
    }
  );
}

/**
 * Predictive Search
 * Fetches search results as the user types; caches responses to avoid redundant requests.
 *
 * @element predictive-search (is="predictive-search")
 * @extends HTMLFormElement
 */
if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      /** @type {Map<string, string>} */ #cache = new Map();
      /** @type {AbortController|null} */ #abortController = null;

      get #input()       { return this.querySelector('input[type="search"]'); }
      get #resetButton() { return this.querySelector('button[type="reset"]'); }
      get #typewriter()  { return this.#input?.previousElementSibling ?? null; }

      connectedCallback() {
        this.#resetButton?.addEventListener('click',  this.#onReset);
        this.#input?.addEventListener('click',        this.#hideTypewriter);
        this.#input?.addEventListener('input',        theme.utils.debounce(this.#onChange, 300));
        this.#input?.addEventListener('focus',        this.#onFocus);
      }

      disconnectedCallback() {
        this.#abortController?.abort();
      }

      #hideTypewriter = () => {
        if (this.#typewriter && !this.#typewriter.hasAttribute('hidden')) {
          this.#typewriter.setAttribute('hidden', '');
        }
      };

      #onReset = (event) => {
        event.preventDefault();
        if (this.#input) this.#input.value = '';
        this.#input?.focus();
        this.removeAttribute('results');
      };

      #onFocus = () => {
        if (!this.closest('.collection')) return;
        document.body.classList.add('predictive-search-open');
        if (this.#getQuery().length === 0) return;
        this.#renderSection(this.#buildUrl().toString());
      };

      #onChange = () => {
        if (this.#getQuery().length === 0) {
          this.#onReset(new Event('click'));
          return;
        }
        this.#hideTypewriter();
        this.#renderSection(this.#buildUrl().toString());
      };

      #getQuery() {
        return this.#input?.value.trim() ?? '';
      }

      #buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);
        url.searchParams.set('q', this.#getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      /** @param {string} url */
      #renderSection(url) {
        if (this.#cache.has(url)) {
          this.#renderFromCache(url);
        } else {
          this.#renderFromFetch(url);
        }
      }

      /** @param {string} url */
      #renderFromCache(url) {
        this.#renderResults(this.#cache.get(url));
        this.setAttribute('results', '');
      }

      /** @param {string} url */
      #renderFromFetch(url) {
        this.#abortController?.abort();
        this.#abortController = new AbortController();
        this.setAttribute('loading', '');

        fetch(url, { signal: this.#abortController.signal })
          .then((r) => r.text())
          .then((html) => {
            this.#renderResults(html);
            this.#cache.set(url, html);
            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((err) => {
            if (err.name !== 'AbortError') console.error(err);
          });
      }

      /** @param {string} html */
      #renderResults(html) {
        const id = `PredictiveSearchResults-${theme.utils.sectionId(this)}`;
        const container = document.getElementById(id);
        if (!container) return;

        container.innerHTML =
          new DOMParser().parseFromString(html, 'text/html').getElementById(id)?.innerHTML ?? '';
      }
    },
    { extends: 'form' }
  );
}

/**
 * Predictive Search Overlay
 * Closes the search overlay when the backdrop is clicked.
 *
 * @element predictive-search-overlay
 * @extends OverlayElement
 */
if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      connectedCallback() {
        this.addEventListener('click', this.#onClick);
      }

      #onClick = () => {
        // Defer so any active input blur fires first.
        setTimeout(() => document.body.classList.remove('predictive-search-open'));
      };
    }
  );
}
